/**
 * Interview-room proctoring. Runs in the browser on the candidate camera.
 * Primary engine: MediaPipe Face Landmarker (CDN). Fallback: Chrome FaceDetector.
 *
 * Flags match the recruiter report:
 * NO_FACE, MULTI_FACE, LOOKING_AWAY, TAB_HIDDEN, NOT_ACTIVE_SPEAKER, LIP_AUDIO_MISMATCH
 */

export const CLIP_FLAGS = new Set([
  'NO_FACE',
  'MULTI_FACE',
  'LOOKING_AWAY',
  'LIP_AUDIO_MISMATCH',
  'NOT_ACTIVE_SPEAKER',
]);

const MEDIAPIPE_VERSION = '0.10.22';
const WASM_ROOT = `https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@${MEDIAPIPE_VERSION}/wasm`;
const MODEL_URL = 'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task';
const TICK_MS = 800;
const NO_FACE_TICKS = 3;
const MULTI_FACE_TICKS = 2;
const LOOK_AWAY_TICKS = 4;
const SILENT_SPEAK_TICKS = 3;
const MIC_SPEAK_LEVEL = 28;
const BLUR_MS = 2000;

const LM = {
  nose: 1,
  leftEye: 33,
  rightEye: 263,
  upperLip: 13,
  lowerLip: 14,
  forehead: 10,
  chin: 152,
};

let landmarkerPromise = null;

async function loadVision() {
  const url = `https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@${MEDIAPIPE_VERSION}/+esm`;
  const mod = await new Function('u', 'return import(u)')(url);
  const root = mod?.FaceLandmarker ? mod : (mod?.default || mod);
  return {
    FilesetResolver: root.FilesetResolver || mod.FilesetResolver,
    FaceLandmarker: root.FaceLandmarker || mod.FaceLandmarker,
  };
}

async function createLandmarker() {
  const vision = await loadVision();
  const fileset = await vision.FilesetResolver.forVisionTasks(WASM_ROOT);
  let lastErr;
  for (const delegate of ['GPU', 'CPU']) {
    try {
      return await vision.FaceLandmarker.createFromOptions(fileset, {
        baseOptions: { modelAssetPath: MODEL_URL, delegate },
        outputFaceBlendshapes: true,
        runningMode: 'VIDEO',
        numFaces: 3,
      });
    } catch (err) {
      lastErr = err;
    }
  }
  throw lastErr || new Error('Face landmarker unavailable');
}

function getLandmarker() {
  if (!landmarkerPromise) {
    landmarkerPromise = createLandmarker().catch(() => {
      landmarkerPromise = null;
      return null;
    });
  }
  return landmarkerPromise;
}

function blendScore(blendshapes, name) {
  const cats = blendshapes?.categories || blendshapes;
  if (!Array.isArray(cats)) return 0;
  const hit = cats.find((c) => (c.categoryName || c.displayName) === name);
  return Number(hit?.score) || 0;
}

function poseFromLandmarks(lm) {
  if (!lm || lm.length < 264) return { yaw: 0, pitch: 0, mouth: 0 };
  const nose = lm[LM.nose];
  const left = lm[LM.leftEye];
  const right = lm[LM.rightEye];
  const upper = lm[LM.upperLip];
  const lower = lm[LM.lowerLip];
  const forehead = lm[LM.forehead];
  const chin = lm[LM.chin];
  const eyeW = Math.abs((right?.x || 0) - (left?.x || 0)) || 0.001;
  const faceH = Math.abs((chin?.y || 0) - (forehead?.y || 0)) || 0.001;
  const midX = ((left?.x || 0) + (right?.x || 0)) / 2;
  const midY = ((forehead?.y || 0) + (chin?.y || 0)) / 2;
  return {
    yaw: ((nose?.x || 0) - midX) / eyeW,
    pitch: ((nose?.y || 0) - midY) / faceH,
    mouth: Math.abs((lower?.y || 0) - (upper?.y || 0)) / eyeW,
  };
}

function mouthFromCanvas(video) {
  if (!video || video.readyState < 2) return 0;
  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 48;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return 0;
  ctx.drawImage(video, 0, 0, 64, 48);
  const data = ctx.getImageData(16, 28, 32, 16).data;
  let sum = 0;
  let sum2 = 0;
  let n = 0;
  for (let i = 0; i < data.length; i += 4) {
    const y = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    sum += y;
    sum2 += y * y;
    n += 1;
  }
  const mean = sum / (n || 1);
  return Math.sqrt(Math.max(0, sum2 / (n || 1) - mean * mean));
}

function createFaceDetector() {
  if (!('FaceDetector' in window)) return null;
  try {
    return new window.FaceDetector({ fastMode: true, maxDetectedFaces: 5 });
  } catch (_) {
    return null;
  }
}

/**
 * @param {object} opts
 * @param {() => HTMLVideoElement | null} opts.getVideo
 * @param {() => number} opts.getMicLevel
 * @param {() => { live: boolean, listening: boolean, aiSpeaking: boolean, cameraOn: boolean, micOn: boolean }} opts.getContext
 * @param {(type: string, detail: string, confidence?: number) => void} opts.onFlag
 * @param {(status: object) => void} [opts.onStatus]
 * @returns {() => void} stop
 */
export function startInterviewProctor({ getVideo, getMicLevel, getContext, onFlag, onStatus }) {
  let stopped = false;
  let landmarker = null;
  let detector = null;
  let engine = 'none';
  let lastTs = 0;
  let noFaceTicks = 0;
  let multiTicks = 0;
  let lookTicks = 0;
  let silentTicks = 0;
  let noFaceOpen = false;
  let multiOpen = false;
  let lookOpen = false;
  let blurTimer = null;
  let lastStatusAt = 0;

  const emitStatus = (status) => {
    if (!onStatus) return;
    const now = Date.now();
    if (now - lastStatusAt < 400 && status.faces === undefined) return;
    lastStatusAt = now;
    onStatus(status);
  };

  const flag = (type, detail, confidence) => {
    const ctx = getContext() || {};
    if (!ctx.live) return;
    onFlag(type, detail, confidence);
  };

  const openFlag = (open, type, detail, confidence) => {
    if (open.value) return;
    open.value = true;
    flag(type, detail, confidence);
  };

  const noFaceLatch = { get value() { return noFaceOpen; }, set value(v) { noFaceOpen = v; } };
  const multiLatch = { get value() { return multiOpen; }, set value(v) { multiOpen = v; } };
  const lookLatch = { get value() { return lookOpen; }, set value(v) { lookOpen = v; } };

  const onVis = () => {
    if (document.hidden) flag('TAB_HIDDEN', 'Candidate left the interview tab');
  };

  const onBlur = () => {
    if (blurTimer) clearTimeout(blurTimer);
    blurTimer = setTimeout(() => {
      blurTimer = null;
      if (document.hidden) return;
      if (document.hasFocus()) return;
      flag('TAB_HIDDEN', 'Candidate left the interview window');
    }, BLUR_MS);
  };

  const onFocus = () => {
    if (blurTimer) {
      clearTimeout(blurTimer);
      blurTimer = null;
    }
  };

  document.addEventListener('visibilitychange', onVis);
  window.addEventListener('blur', onBlur);
  window.addEventListener('focus', onFocus);

  const tick = async () => {
    if (stopped) return;
    const ctx = getContext() || {};
    const video = getVideo();
    if (!ctx.live) {
      noFaceTicks = 0;
      multiTicks = 0;
      lookTicks = 0;
      silentTicks = 0;
      noFaceOpen = false;
      multiOpen = false;
      lookOpen = false;
      emitStatus({ live: false, faces: 0, lookingAway: false, engine });
      return;
    }

    if (!ctx.cameraOn) {
      noFaceTicks += 1;
      multiTicks = 0;
      lookTicks = 0;
      silentTicks = 0;
      multiOpen = false;
      lookOpen = false;
      if (noFaceTicks >= NO_FACE_TICKS) openFlag(noFaceLatch, 'NO_FACE', 'Camera is off');
      emitStatus({ live: true, faces: 0, lookingAway: false, cameraOff: true, engine });
      return;
    }

    if (!video || video.readyState < 2 || !video.videoWidth) {
      noFaceTicks += 1;
      if (noFaceTicks >= NO_FACE_TICKS) openFlag(noFaceLatch, 'NO_FACE', 'No camera frame');
      emitStatus({ live: true, faces: 0, lookingAway: false, engine });
      return;
    }

    let faces = 0;
    let lookingAway = false;
    let mouthOpen = 0;
    let checked = false;

    if (landmarker) {
      try {
        const ts = Math.max(performance.now(), lastTs + 1);
        lastTs = ts;
        const result = landmarker.detectForVideo(video, ts);
        const lms = result?.faceLandmarks || [];
        faces = lms.length;
        checked = true;
        if (faces > 0) {
          const pose = poseFromLandmarks(lms[0]);
          const jaw = blendScore(result?.faceBlendshapes?.[0], 'jawOpen');
          mouthOpen = Math.max(pose.mouth * 2.2, jaw);
          lookingAway = Math.abs(pose.yaw) > 0.42 || Math.abs(pose.pitch) > 0.38;
        }
      } catch (_) {
        checked = false;
      }
    }

    // A MediaPipe miss is not the end of the check. The browser detector
    // looks at the same frame before the room is allowed to say there is no face.
    if (faces === 0 && detector) {
      try {
        const found = await detector.detect(video);
        faces = found?.length || 0;
        checked = true;
        if (faces > 0) mouthOpen = mouthFromCanvas(video) / 18;
      } catch (_) { /* frame unsupported */ }
    }

    if (!checked) {
      noFaceTicks = 0;
      noFaceOpen = false;
      emitStatus({ live: true, unavailable: true, lookingAway: false, engine: engine || 'none' });
      return;
    }

    if (faces === 0) {
      noFaceTicks += 1;
      multiTicks = 0;
      lookTicks = 0;
      multiOpen = false;
      lookOpen = false;
      if (noFaceTicks >= NO_FACE_TICKS) openFlag(noFaceLatch, 'NO_FACE', 'No face visible in camera');
    } else {
      noFaceTicks = 0;
      noFaceOpen = false;
      if (faces >= 2) {
        multiTicks += 1;
        if (multiTicks >= MULTI_FACE_TICKS) openFlag(multiLatch, 'MULTI_FACE', `${faces} faces in frame`);
      } else {
        multiTicks = 0;
        multiOpen = false;
      }
      if (lookingAway) {
        lookTicks += 1;
        if (lookTicks >= LOOK_AWAY_TICKS) {
          openFlag(lookLatch, 'LOOKING_AWAY', 'Candidate is looking away from the camera');
        }
      } else {
        lookTicks = 0;
        lookOpen = false;
      }
    }

    const micLevel = Number(getMicLevel()) || 0;
    const canCheckLips = ctx.micOn && !ctx.aiSpeaking && ctx.listening && faces === 1;
    if (canCheckLips && micLevel >= MIC_SPEAK_LEVEL && mouthOpen < 0.12) {
      silentTicks += 1;
      if (silentTicks >= SILENT_SPEAK_TICKS) {
        flag('NOT_ACTIVE_SPEAKER', 'Speech on mic but the on-camera face is not talking', 0.65);
        flag('LIP_AUDIO_MISMATCH', 'Mouth movement does not match microphone audio', 0.6);
        silentTicks = 0;
      }
    } else {
      silentTicks = 0;
    }

    emitStatus({
      live: true,
      faces,
      lookingAway,
      mouthOpen,
      engine,
    });
  };

  const boot = async () => {
    try {
      landmarker = await getLandmarker();
      detector = createFaceDetector();
      engine = landmarker ? 'mediapipe' : (detector ? 'facedetector' : 'none');
    } catch (_) {
      detector = createFaceDetector();
      engine = detector ? 'facedetector' : 'none';
    }
    if (!stopped) tick();
  };

  boot();
  const id = setInterval(tick, TICK_MS);

  return () => {
    stopped = true;
    clearInterval(id);
    if (blurTimer) clearTimeout(blurTimer);
    document.removeEventListener('visibilitychange', onVis);
    window.removeEventListener('blur', onBlur);
    window.removeEventListener('focus', onFocus);
  };
}
